import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, Trash2, Video } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export default function MeetingTrash() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingPurge, setPendingPurge] = useState(null);
  const [busy, setBusy] = useState(null); // meetingId of in-flight op

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/meetings/trash");
      setItems(r.data);
    } catch (e) {
      toast.error("Could not load trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (m) => {
    setBusy(m.id);
    try {
      await api.post(`/meetings/${m.id}/restore`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      toast.success(`"${m.title || 'Session'}" restored to the archive.`);
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setBusy(null);
    }
  };

  const confirmPurge = async () => {
    if (!pendingPurge) return;
    setBusy(pendingPurge.id);
    try {
      await api.delete(`/meetings/${pendingPurge.id}/purge`);
      setItems((prev) => prev.filter((x) => x.id !== pendingPurge.id));
      toast.success("Session permanently deleted.");
      setPendingPurge(null);
    } catch (e) {
      toast.error("Permanent delete failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-10" data-testid="meeting-trash-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Link to="/meetings" className="label-eyebrow text-[#666] hover:text-[#c89f45] transition-colors inline-flex items-center gap-1" data-testid="trash-back">
            <ArrowLeft size={12} /> Back to archive
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Trash</h1>
          <p className="text-[#888] mt-4 max-w-xl">
            Deleted sessions live here for 30 days. Restore them anytime before the countdown ends —
            after that, they're permanently purged along with the recording.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="surface rounded-md p-8 text-sm text-[#666]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-md p-16 text-center" data-testid="trash-empty">
          <p className="font-serif text-2xl">Nothing in the trash.</p>
          <p className="text-sm text-[#888] mt-2">Any session you delete will land here first.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => {
            const daysLeft = daysUntil(m.restore_before);
            return (
              <div
                key={m.id}
                className="surface rounded-md p-6 flex items-center justify-between gap-4"
                data-testid={`trash-row-${m.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="label-eyebrow">{m.cadence} · {m.meeting_type}</span>
                    <span
                      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                        daysLeft != null && daysLeft <= 3
                          ? "bg-[#2a1a1a] text-[#e07b6a]"
                          : "bg-[#1a1a1a] text-[#888]"
                      }`}
                      data-testid={`trash-countdown-${m.id}`}
                    >
                      {daysLeft != null ? `PURGES IN ${daysLeft} DAY${daysLeft === 1 ? "" : "S"}` : "PURGE PENDING"}
                    </span>
                    {m.recording_path && (
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#2a1a1a] text-[#e07b6a] flex items-center gap-1"
                        title="Has a recording"
                      >
                        <Video size={9} />
                        REC
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl mt-2 truncate">{m.title}</h3>
                  <p className="text-xs text-[#666] mt-2 font-mono">
                    Deleted {new Date(m.deleted_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => restore(m)}
                    disabled={busy === m.id}
                    className="bg-transparent border-[#c89f45]/50 text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] hover:border-[#c89f45] rounded-md h-9"
                    data-testid={`trash-restore-${m.id}`}
                  >
                    <RotateCcw size={14} className="mr-1.5" />
                    Restore
                  </Button>
                  <button
                    type="button"
                    onClick={() => setPendingPurge(m)}
                    disabled={busy === m.id}
                    className="h-9 w-9 rounded-md border border-transparent text-[#666] hover:text-[#e07b6a] hover:border-[#e07b6a]/40 hover:bg-[#2a1a1a]/40 disabled:opacity-40 flex items-center justify-center transition-colors"
                    aria-label="Purge permanently"
                    title="Delete permanently"
                    data-testid={`trash-purge-${m.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!pendingPurge} onOpenChange={(o) => !o && setPendingPurge(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa]" data-testid="trash-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Permanently delete this session?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#888] pt-2">
              {pendingPurge?.title ? `"${pendingPurge.title}" — ` : ""}
              This skips the 30-day trash window. The recording, transcript, minutes, and any
              action items generated from this session will be gone immediately with no recovery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white"
              data-testid="trash-purge-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPurge}
              disabled={busy === pendingPurge?.id}
              className="bg-[#e07b6a] hover:bg-[#c96a5a] text-[#0a0a0a] font-medium"
              data-testid="trash-purge-confirm"
            >
              {busy === pendingPurge?.id ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
